import { useState } from 'react'
import { Download } from 'lucide-react'
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { usePermission } from '@/hooks/usePermission'
import { apiClient } from '@/services/apiClient'

function toDateInput(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * SA-21: deployment-wide conversations CSV export (admin reporting).
 * Hidden without conversations:export. Downloads via apiClient + blob —
 * a bare <a href> cannot carry the in-memory Bearer token and 401s.
 */
export function ConversationsExportControl() {
  const canExport = usePermission('conversations:export')
  const now = new Date()
  const [start, setStart] = useState(
    toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
  )
  const [end, setEnd] = useState(toDateInput(now))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!canExport) return null

  async function download() {
    setError(null)
    setBusy(true)
    try {
      const qs = new URLSearchParams({ start, end })
      const res = await apiClient.fetch(`/api/conversations/export?${qs}`)
      if (!res.ok) {
        let detail = `Export failed (${res.status})`
        try {
          const body = await res.json()
          if (typeof body?.detail === 'string') detail = body.detail
        } catch { /* keep generic message */ }
        setError(detail)
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `conversations_${start}_${end}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      setError('Export failed — check your connection and try again')
    } finally {
      setBusy(false)
    }
  }

  const rangeInvalid = !start || !end || end < start

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3">
        <div className="space-y-1">
          <label htmlFor="export-start" className="text-xs font-medium text-muted-foreground">
            From
          </label>
          <Input
            id="export-start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="export-end" className="text-xs font-medium text-muted-foreground">
            To
          </label>
          <Input
            id="export-end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <Button
          size="sm"
          className="w-full"
          disabled={busy || rangeInvalid}
          onClick={download}
        >
          {busy ? 'Exporting…' : 'Download'}
        </Button>
      </PopoverContent>
    </Popover>
  )
}
